import { useMutation, useQueryClient } from '@tanstack/react-query';
import type { AxiosInstance } from 'axios';
import { type ApiError, addActionNameToMutationResult, useAxiosInstance } from 'shared/hooks/utils';
import { z } from 'zod/v4';
import { CustomVariableSchema, DisplayTypeSchema } from './use-load-custom-variables';

const URL = '/custom_variables';

export const UpdateCustomVariableVariablesSchema = z.object({
  id: z.number(),
  data: z
    .object({
      name: z.string().min(1),
      api: z.boolean(),
      webhook: z.boolean(),
      display: DisplayTypeSchema.nullish(),
      defaultValue: z.union([z.string(), z.number(), z.boolean()]).nullish(),
    })
    .partial(),
});

export type UpdateCustomVariableVariables = z.infer<typeof UpdateCustomVariableVariablesSchema>;

export async function updateCustomVariableFn(
  axiosInstance: Promise<AxiosInstance>,
  variables: UpdateCustomVariableVariables,
) {
  const parsed = UpdateCustomVariableVariablesSchema.parse(variables);
  const instance = await axiosInstance;
  const { data } = await instance.put(`${URL}/${parsed.id}`, parsed.data);
  return CustomVariableSchema.parse(data);
}

export function useUpdateCustomVariable() {
  const client = useQueryClient();
  const axiosInstance = useAxiosInstance();

  const mutation = useMutation<
    z.infer<typeof CustomVariableSchema>,
    ApiError,
    UpdateCustomVariableVariables
  >({
    mutationFn: variables => updateCustomVariableFn(axiosInstance, variables),
    meta: { suppressSuccessNotification: true },
    onSuccess: () => {
      client.invalidateQueries({ queryKey: ['customVariables'] });
      client.invalidateQueries({ queryKey: ['company-configuration'] });
    },
  });

  return addActionNameToMutationResult<
    z.infer<typeof CustomVariableSchema>,
    ApiError,
    UpdateCustomVariableVariables,
    'updateCustomVariable'
  >('updateCustomVariable', mutation);
}
